/**
 * ContactsPage
 *
 * Full contacts management: searchable list, detail panel, contact groups,
 * favorites, create/edit/delete dialogs.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Search,
  Plus,
  Star,
  Users,
  Trash2,
  Edit,
  Mail,
  Phone,
  Building2,
  X,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useEmailAccounts } from '../hooks/useEmailAccounts';
import { useContacts } from '../hooks/useContacts';
import { useContactGroups } from '../hooks/useContactGroups';
import type { EmailContact } from '@/lib/api';

export function ContactsPage() {
  const { activeAccount } = useEmailAccounts();
  const accountId = activeAccount?.id;

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'name' | 'lastContacted' | 'contactCount'>('name');
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editContact, setEditContact] = useState<EmailContact | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailContact | null>(null);
  const [showGroupsPanel, setShowGroupsPanel] = useState(false);

  const { contacts, loading, createContact, updateContact, deleteContact, toggleFavorite } =
    useContacts({ accountId, search, sort });
  const { groups, createGroup, deleteGroup } = useContactGroups(accountId);
  const { toast } = useToast();

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === selectedContactId) ?? null,
    [contacts, selectedContactId],
  );

  // Form state for create/edit
  const [formEmail, setFormEmail] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formCompany, setFormCompany] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const resetForm = useCallback(() => {
    setFormEmail('');
    setFormDisplayName('');
    setFormFirstName('');
    setFormLastName('');
    setFormCompany('');
    setFormPhone('');
    setFormNotes('');
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setEditContact(null);
    setShowCreateDialog(true);
  }, [resetForm]);

  const openEdit = useCallback((c: EmailContact) => {
    setFormEmail(c.emailAddress);
    setFormDisplayName(c.displayName || '');
    setFormFirstName(c.firstName || '');
    setFormLastName(c.lastName || '');
    setFormCompany(c.company || '');
    setFormPhone(c.phone || '');
    setFormNotes(c.notes || '');
    setEditContact(c);
    setShowCreateDialog(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!accountId) return;
    try {
      if (editContact) {
        await updateContact(editContact.id, {
          emailAddress: formEmail,
          displayName: formDisplayName || undefined,
          firstName: formFirstName || undefined,
          lastName: formLastName || undefined,
          company: formCompany || undefined,
          phone: formPhone || undefined,
          notes: formNotes || undefined,
        });
        toast({ title: 'Contact updated' });
      } else {
        await createContact({
          accountId,
          emailAddress: formEmail,
          displayName: formDisplayName || undefined,
          firstName: formFirstName || undefined,
          lastName: formLastName || undefined,
          company: formCompany || undefined,
          phone: formPhone || undefined,
          notes: formNotes || undefined,
        });
        toast({ title: 'Contact created' });
      }
      setShowCreateDialog(false);
      resetForm();
    } catch (err) {
      toast({
        title: 'Failed to save contact',
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  }, [
    accountId,
    editContact,
    formEmail,
    formDisplayName,
    formFirstName,
    formLastName,
    formCompany,
    formPhone,
    formNotes,
    createContact,
    updateContact,
    toast,
    resetForm,
  ]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteContact(deleteTarget.id);
      if (selectedContactId === deleteTarget.id) setSelectedContactId(null);
      toast({ title: 'Contact deleted' });
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
    setDeleteTarget(null);
  }, [deleteTarget, deleteContact, selectedContactId, toast]);

  const [newGroupName, setNewGroupName] = useState('');

  return (
    <div className="flex h-full">
      {/* Contact list */}
      <div className="flex flex-col w-80 border-r border-border flex-shrink-0">
        {/* Header */}
        <div className="p-3 border-b border-border space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Contacts</h2>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setShowGroupsPanel(!showGroupsPanel)}
                title="Groups"
              >
                <Users className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={openCreate}
                title="Add contact"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts..."
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort by name</SelectItem>
              <SelectItem value="lastContacted">Last contacted</SelectItem>
              <SelectItem value="contactCount">Frequency</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-10">
              <Users className="h-10 w-10 mb-2 opacity-30" />
              <p className="text-sm">No contacts found</p>
            </div>
          ) : (
            contacts.map((c) => (
              <button
                key={c.id}
                className={cn(
                  'flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-muted/50 transition-colors border-b border-border',
                  selectedContactId === c.id && 'bg-muted',
                )}
                onClick={() => setSelectedContactId(c.id)}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  {(c.displayName || c.emailAddress).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {c.displayName || c.emailAddress}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{c.emailAddress}</div>
                </div>
                {c.isFavorite && (
                  <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Detail panel or groups panel */}
      <div className="flex-1 min-w-0">
        {showGroupsPanel ? (
          /* Groups panel */
          <div className="p-6 max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Contact Groups</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setShowGroupsPanel(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="New group name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                disabled={!newGroupName.trim() || !accountId}
                onClick={async () => {
                  if (!accountId) return;
                  await createGroup({ accountId, name: newGroupName });
                  setNewGroupName('');
                  toast({ title: 'Group created' });
                }}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-2">
              {groups.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center justify-between px-3 py-2 border border-border rounded-md"
                >
                  <div>
                    <div className="text-sm font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{g.memberCount} members</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => deleteGroup(g.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {groups.length === 0 && (
                <p className="text-sm text-muted-foreground">No groups yet</p>
              )}
            </div>
          </div>
        ) : selectedContact ? (
          /* Contact detail */
          <div className="p-6 max-w-md">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-semibold flex-shrink-0">
                {(selectedContact.displayName || selectedContact.emailAddress)
                  .charAt(0)
                  .toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold truncate">
                  {selectedContact.displayName || selectedContact.emailAddress}
                </h3>
                {selectedContact.company && (
                  <p className="text-sm text-muted-foreground">{selectedContact.company}</p>
                )}
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => toggleFavorite(selectedContact.id)}
                  >
                    <Star
                      className={cn(
                        'h-3.5 w-3.5',
                        selectedContact.isFavorite && 'fill-yellow-400 text-yellow-400',
                      )}
                    />
                    {selectedContact.isFavorite ? 'Unfavorite' : 'Favorite'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => openEdit(selectedContact)}
                  >
                    <Edit className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1 text-destructive"
                    onClick={() => setDeleteTarget(selectedContact)}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{selectedContact.emailAddress}</span>
              </div>
              {selectedContact.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedContact.phone}</span>
                </div>
              )}
              {selectedContact.company && (
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedContact.company}</span>
                </div>
              )}
              {selectedContact.notes && (
                <div className="mt-4 p-3 bg-muted rounded-md text-sm">{selectedContact.notes}</div>
              )}
              <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                <span>Contacted {selectedContact.contactCount} times</span>
                {selectedContact.lastContactedAt && (
                  <span>
                    Last: {new Date(selectedContact.lastContactedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a contact to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editContact ? 'Edit Contact' : 'Create Contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Email *</Label>
              <Input
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">First name</Label>
                <Input value={formFirstName} onChange={(e) => setFormFirstName(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm">Last name</Label>
                <Input value={formLastName} onChange={(e) => setFormLastName(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-sm">Display name</Label>
              <Input value={formDisplayName} onChange={(e) => setFormDisplayName(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Company</Label>
              <Input value={formCompany} onChange={(e) => setFormCompany(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Phone</Label>
              <Input value={formPhone} onChange={(e) => setFormPhone(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formEmail.includes('@')}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteTarget?.displayName || deleteTarget?.emailAddress}
              .
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
